import styled from "@emotion/styled/macro";
import { tokens, Typography } from "@sunrun/experience-ui-components";
import { useMediaQuery } from "react-responsive";
import customerAppImage from "components/assets/images/customer-app.png";
import sunrunAppQrcode from "components/assets/images/sunrun-app-qrcode.png";
import sunrunMobile from "cap/assets/sunrunMobile.svg";
import appStoreIcon from "components/assets/images/app-store.png";
import googlePlayIcon from "components/assets/images/google-play.png";
import { Box } from "components/atoms/Box";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";

type DownloadCustomerAppProps = {
  isCompactSize?: boolean;
  isPartner?: boolean;
};

const FullsizeDownloadCustomerApp = ({
  isPartner,
}: DownloadCustomerAppProps) => {
  const isSingleColumn = useMediaQuery({ maxWidth: 1024 });
  const { isNonSolarOffer } = useCheckoutContext();
  if (isSingleColumn) {
    return (
      <StyledBackground
        width="100%"
        height="490px"
        maxWidth="341px"
        overflow="hidden"
        data-testid="download-customer-app-full-size-single-column"
      >
        <Box
          display="flex"
          flexDirection="column"
          padding="16px"
          paddingTop="30px"
        >
          <Box display="flex" flexDirection="column" gap="8px">
            <StyledTypography size={tokens.FONT_SIZE_4} color={tokens.WHITE}>
              Stay up-to-date on your{" "}
              {isNonSolarOffer ? "Sunrun installation" : "solar"} progress
            </StyledTypography>
            <StyledTypography size={tokens.FONT_SIZE_0} color={tokens.WHITE}>
              Download the Sunrun app to track your progress and a whole lot
              more
            </StyledTypography>
          </Box>
          <Box display="flex" marginTop="24px" gap="24px">
            <img
              width="182px"
              src={customerAppImage}
              alt="iphone customer app"
            />
            <Box
              display="flex"
              flexDirection="column"
              gap="8px"
              marginTop="12px"
            >
              <img width="88px" src={sunrunMobile} alt="sunrun mobile" />
              <img width="88px" src={sunrunAppQrcode} alt="sunrun app qrcode" />

              <a
                href="https://apps.apple.com/us/app/sunrun/id6443806453"
                target="_blank"
                rel="noreferrer noopener"
              >
                <img width="88.51px" src={appStoreIcon} alt="app store" />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.sunrun.customerapp"
                target="_blank"
                rel="noreferrer noopener"
              >
                <img width="88.51px" src={googlePlayIcon} alt="google play" />
              </a>
            </Box>
          </Box>
        </Box>
        {!isPartner && <DemoPromptComponent isSingleColumn={true} />}
      </StyledBackground>
    );
  }

  return (
    <StyledBackground
      width="100%"
      height="400px"
      maxWidth="750px"
      marginBottom="50px"
      data-testid="download-customer-app-full-size-two-column"
    >
      <Box display="flex">
        <Box marginLeft="47px" marginTop="18px">
          <img width="203px" src={customerAppImage} alt="iphone customer app" />
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          margin="56px"
          marginTop="38px"
          marginBottom="0px"
          gap="8px"
        >
          <StyledTypography size={tokens.FONT_SIZE_4} color={tokens.WHITE}>
            Stay up-to-date on your{" "}
            {isNonSolarOffer ? "Sunrun installation" : "solar"} progress
          </StyledTypography>
          <StyledTypography size={tokens.FONT_SIZE_0} color={tokens.WHITE}>
            Download the Sunrun app to track your progress and a whole lot more
          </StyledTypography>
          <Box display="flex" gap="16px" marginTop="24px">
            <img
              width="94px"
              height="94px"
              src={sunrunMobile}
              alt="sunrun mobile"
            />
            <img
              width="94px"
              height="94px"
              src={sunrunAppQrcode}
              alt="sunrun app qrcode"
            />
          </Box>
          <Box display="flex" gap="16px" marginTop="0px">
            <a
              href="https://apps.apple.com/us/app/sunrun/id6443806453"
              target="_blank"
              rel="noreferrer noopener"
            >
              <img
                width="151px"
                height="45px"
                src={appStoreIcon}
                alt="app store"
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.sunrun.customerapp"
              target="_blank"
              rel="noreferrer noopener"
            >
              <img
                width="151px"
                height="45px"
                src={googlePlayIcon}
                alt="google play"
              />
            </a>
          </Box>
          {!isPartner && <DemoPromptComponent isSingleColumn={false} />}
        </Box>
      </Box>
    </StyledBackground>
  );
};

const ReducedSizeDownloadCustomerApp = () => {
  return (
    <StyledBackground
      width="223px"
      height="233px"
      data-testid="download-customer-app-reduced-size"
    >
      <Box display="flex">
        <Box display="flex" flexDirection="column" gap="11px" margin="17px">
          <Typography
            size={tokens.FONT_SIZE_1}
            color={tokens.WHITE}
            lineHeight={19.2}
          >
            Download the Sunrun customer app to stay updated on your progress
          </Typography>
          <Box display="flex" gap="8px">
            <img
              width="88px"
              height="88px"
              src={sunrunMobile}
              alt="sunrun mobile"
            />
            <img
              width="88px"
              height="88px"
              src={sunrunAppQrcode}
              alt="sunrun app qrcode"
            />
          </Box>
          <Box display="flex" gap="8px">
            <a
              href="https://apps.apple.com/us/app/sunrun/id6443806453"
              target="_blank"
              rel="noreferrer noopener"
            >
              <img
                width="88px"
                height="28px"
                src={appStoreIcon}
                alt="app store"
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.sunrun.customerapp"
              target="_blank"
              rel="noreferrer noopener"
            >
              <img
                width="88px"
                height="28px"
                src={googlePlayIcon}
                alt="google play"
              />
            </a>
          </Box>
        </Box>
      </Box>
    </StyledBackground>
  );
};

const DownloadCustomerApp = ({
  isCompactSize = false,
  isPartner = false,
}: DownloadCustomerAppProps) => {
  return (
    <>
      {isCompactSize ? (
        <ReducedSizeDownloadCustomerApp />
      ) : (
        <FullsizeDownloadCustomerApp isPartner={isPartner} />
      )}
    </>
  );
};

const DemoPromptComponent = ({
  isSingleColumn,
}: {
  isSingleColumn: boolean;
}) => {
  return (
    <DemoBox singleColumn={isSingleColumn}>
      <DemoPrompt singleColumn={isSingleColumn}>
        <Typography size={16} color={tokens.BRAND_HEROBLUE}>
          Demo the Sunrun Customer App now!
        </Typography>
        <LearnMoreLink
          href="https://current.sunrun.com/site/7b6848bd-8bfb-5e2e-b56d-41b94260487d/page/c0b98bac-6808-54d5-97c3-46d39d3769b9#accessing-the-app-heading_1n0g"
          target="_blank"
          rel="noreferrer noopener"
        >
          Learn more
        </LearnMoreLink>
      </DemoPrompt>
    </DemoBox>
  );
};

const StyledBackground = styled.div<{
  height?: string;
  width?: string;
  maxWidth?: string;
  overflow?: string;
  marginBottom?: string;
}>`
  background: ${tokens.BRAND_NIGHT_DARKER};
  border-radius: 16px;
  height: ${({ height }) => height};
  width: ${({ width }) => width};
  max-width: ${({ maxWidth }) => maxWidth};
  overflow: ${({ overflow }) => overflow};
  margin-bottom: ${({ marginBottom }) => marginBottom};
  position: relative;
`;

const StyledTypography = styled(Typography)`
  line-height: 110% !important;
`;

const DemoPrompt = styled.div<{ singleColumn?: boolean }>`
  background: ${tokens.BRAND_CHARGEBLUE};
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  text-align: left;
  p {
    line-height: 110%;
  }
  align-items: left;
  justify-content: left;
  padding: 16px;
  ${({ singleColumn }) =>
    singleColumn &&
    `
    margin: 0 auto 0 auto;
    padding: 8px;
    width: 90%;
  `}
`;

const DemoBox = styled.div<{ singleColumn?: boolean }>`
  white-space: nowrap;
  min-width: fit-content;
  ${({ singleColumn }) =>
    singleColumn &&
    `
    width: 100%;
      background: ${tokens.BRAND_NIGHT_DARKER};
  border-radius:  0 0 16px 16px;
      position: absolute;
    left: 0;
    bottom: 0;
    padding-bottom: 8px;
  `}
`;

const LearnMoreLink = styled.a`
  color: ${tokens.BRAND_HEROBLUE};
  text-decoration: underline;
`;

export { DownloadCustomerApp };
